import Icon from '@ant-design/icons';
import { SortOrder } from 'antd/es/table/interface';
import moment from 'moment-timezone';
import { ActionType, ProColumns, RequestData } from '@ant-design/pro-table';
import { Button, Space, Typography, Tag } from 'antd';
import React, { useRef } from 'react';
import Stripe from 'stripe';
import { useTableInvoiceRow } from '../../../../../hooks/stripe';
import { getSorterParams, queryFilterParams } from '../../../../../utils';
import { Download } from '../../../../Common/Icon';

import Table from '../../../../Common/Table';
import { formatAmount } from '../../../../../utils/formatAmount';

const getInvoiceStatus = (inv: Stripe.Invoice) => {
  const charge = inv.charge as Stripe.Charge;

  if (charge.amount_refunded) {
    return { key: 'refunded', label: 'Refunded', color: 'default' };
  }

  return { key: 'paid', label: 'Paid', color: 'green' };
};

function InvoicesTable(): JSX.Element {
  const invoicesGet = useTableInvoiceRow();
  const actionRef = useRef<ActionType>();

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number | undefined;
      current?: number | undefined;
      keyword?: string | undefined;
    },
    sorter: Record<string, SortOrder>
  ): Promise<Partial<RequestData<Stripe.Invoice>>> => {
    const newParams = queryFilterParams({
      page: current ? `${current}` : '1',
      limit: pageSize ? `${pageSize}` : '100',
      ...args,
      ...getSorterParams(sorter),
    });

    return invoicesGet.fetch(newParams).then((data) => {
      if (data) {
        const { invoices, total } = data;

        return { data: invoices || [], success: true, total };
      }

      return { data: [], success: false, total: 0 };
    });
  };

  const columns: ProColumns<Stripe.Invoice>[] = [
    {
      title: 'DATE',
      dataIndex: 'created',
      sorter: false,
      renderText: (created) => moment.unix(created).format('MMM D, YYYY, h:mm A'),
    },
    {
      title: 'NAME',
      dataIndex: 'number',
      sorter: false,
    },
    {
      title: 'PRICE',
      dataIndex: 'subtotal',
      sorter: false,
      renderText: (subtotal, invoice) => {
        const discount = invoice?.discount;
        const originalPrice = subtotal / 100;
        const discountedPrice = originalPrice * ((discount?.coupon?.percent_off || 0) / 100);

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ textDecoration: discount ? 'line-through' : 'none' }}>
              {formatAmount(originalPrice, { showCurrency: true, max: 2, min: 2 })}
            </span>
            {discount && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ color: '#059669', fontWeight: '600' }}>
                  {formatAmount(discountedPrice, { showCurrency: true, max: 2, min: 2 })}
                </span>
                <span
                  style={{
                    background: '#ecfdf5',
                    color: '#065f46',
                    borderRadius: 12,
                    padding: '2px 6px',
                    fontSize: 12,
                    flexShrink: 0
                  }}
                >
                  {discount.coupon.percent_off}% off
                </span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      sorter: false,
      render: (_, invoice) => {
        const { color, label } = getInvoiceStatus(invoice);

        return (
          <Tag color={color} style={{ width: 'fit-content' }}>
            {label}
          </Tag>
        );
      },
    },
    {
      title: 'ACTION',
      sorter: false,
      align: 'right',
      render: (node, invoice) => (
        <Button
          style={{ marginLeft: 'auto', display: 'block', textAlign: 'right' }}
          type="ghost"
          className="color-gray"
          icon={<Icon component={Download} />}
          href={invoice.invoice_pdf || undefined}
          download
        >
          DOWNLOAD INVOICE
        </Button>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={12}>
      <Typography.Title level={2}>Billing History</Typography.Title>
      <Table<Stripe.Invoice>
        search={false}
        columns={columns}
        request={tableRequest}
        actionRef={actionRef}
        showSorterTooltip={false}
        toolBarRender={false}
        pagination={false}
      />
    </Space>
  );
}

export default InvoicesTable;
